import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"
import PropTypes from "prop-types"
import { Formik, Form } from "formik"
import { Modal, ModalBody } from "reactstrap"
import { toast, Bounce } from "react-toastify"
import { useSelector } from "react-redux"
import CustomButton from "../OnboardingComponents/CustomButton"
import CustomInput from "../OnboardingComponents/CustomInput"
import RoleSelect from "../OnboardingComponents/RoleSelect"
import CategoriesMultiselect from "../OnboardingComponents/CategoriesMultiselect"
import CustomDatePicker from "../OnboardingComponents/CustomDatePicker"
import { ICONS } from "../OnboardingComponents/icon"
import useCategories from "../../hooks/useCategories"
import { post, patch } from "../../api/axios"
import endpoints from "../../api/endpoints"
import { toastApiError } from "../../helpers/apiError"
import PreferredCitySelect from "../Common/PreferredCitySelect"
import { getUserAddressCityOptions } from "../../pages/Profile/profileUser"
import { fetchOngoingRoleById } from "../../pages/Profile/profileProjects"
import {
  getUserCityHubLevel,
  isCityTierAccessible,
} from "../../pages/CityHub/cityHubCity"
import {
  emptyOngoingRole,
  ongoingRoleSchema,
  toLocalOngoingRole,
  toOngoingRolePayload,
} from "../../helpers/ongoingRoleForm"

const AddOngoingRoleModalContext = createContext(null)

const TOAST_OPTIONS = {
  position: "bottom-center",
  theme: "colored",
  transition: Bounce,
}

const AddOngoingRoleModal = ({
  isOpen,
  onClose,
  role = null,
  onSuccess,
}) => {
  const { categories } = useCategories()
  const currentUser = useSelector(state => state.auth.currentUser)
  const cityOptions = useMemo(() => {
    const userLevel = getUserCityHubLevel(currentUser)
    const addresses = Array.isArray(currentUser?.addresses)
      ? currentUser.addresses
      : []

    // Same unlock rules as City Hub / Create Project: Primary L1, Secondary L2, Tertiary L3.
    return getUserAddressCityOptions(currentUser).filter(option => {
      const address = addresses.find(
        item =>
          String(item?.city_id || item?.city?.id) === String(option.value),
      )
      return isCityTierAccessible(address?.address_type, userLevel)
    })
  }, [currentUser])
  const isEdit = Boolean(role?.id)
  const [detailRole, setDetailRole] = useState(null)
  const [isLoadingDetail, setIsLoadingDetail] = useState(false)

  useEffect(() => {
    if (!isOpen || !role?.id) {
      setDetailRole(null)
      setIsLoadingDetail(false)
      return undefined
    }

    let cancelled = false
    // Prefill from list/profile row immediately, then replace with API detail.
    setDetailRole(role)
    setIsLoadingDetail(true)

    const load = async () => {
      const fetched = await fetchOngoingRoleById(role.id)
      if (cancelled) return
      if (fetched) setDetailRole(fetched)
      setIsLoadingDetail(false)
    }

    load()
    return () => {
      cancelled = true
    }
  }, [isOpen, role])

  const initialValues = useMemo(() => {
    if (!isEdit) return emptyOngoingRole()
    return toLocalOngoingRole(detailRole || role)
  }, [isEdit, detailRole, role])

  const handleSubmit = async (values, { setSubmitting }) => {
    const payload = {
      roles: [toOngoingRolePayload({ ...values, id: role?.id || values.id })],
    }
    const response = isEdit
      ? await patch(endpoints.onGoingRole, payload, { skipErrorToast: true })
      : await post(endpoints.onGoingRole, payload, { skipErrorToast: true })

    if (!response?.data?.success) {
      toastApiError(response, isEdit ? "Failed to update role" : "Failed to save role")
      setSubmitting(false)
      return
    }

    toast.success(
      response?.data?.message || (isEdit ? "Role updated" : "Role saved"),
      TOAST_OPTIONS,
    )
    setSubmitting(false)
    if (typeof onSuccess === "function") onSuccess()
    onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      centered
      className="amok-modal amok-modal--ongoing-role"
      contentClassName="amok-modal__content"
      backdropClassName="amok-modal__backdrop amok-modal-backdrop--blur"
    >
      <ModalBody className="amok-modal__body ongoing-role-modal__body">
        <Formik
          key={
            isOpen
              ? `ongoing-role-${role?.id || "new"}-${isLoadingDetail ? "loading" : "ready"}`
              : "closed"
          }
          initialValues={initialValues}
          enableReinitialize
          validationSchema={ongoingRoleSchema}
          onSubmit={handleSubmit}
          validateOnBlur
          validateOnChange
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            setFieldValue,
            setFieldTouched,
            isSubmitting,
          }) => (
            <Form className="ongoing-role-modal">
              <div className="ongoing-role-modal__main">
                <div className="ongoing-role-modal__header">
                  <h2 className="ongoing-role-modal__title">
                    {isEdit ? "Edit Ongoing Role" : "Add Ongoing Role"}
                  </h2>
                  <p className="ongoing-role-modal__subtitle">
                    Add roles that are part of your long-term work
                  </p>
                </div>

                <div
                  className="ongoing-role-modal__fields"
                  aria-busy={isLoadingDetail || undefined}
                >
                  <RoleSelect
                    label="Role Title"
                    placeholder="e.g. Music Producer"
                    value={values.roleType}
                    onChange={nextRole => {
                      setFieldValue("roleType", nextRole)
                      setFieldValue("roleTitle", nextRole?.name || "")
                      setFieldTouched("roleType", true, false)
                    }}
                    onBlur={() => setFieldTouched("roleType", true)}
                    error={
                      touched.roleType && typeof errors.roleType === "string"
                        ? errors.roleType
                        : null
                    }
                    menuPortalTarget={
                      typeof document !== "undefined" ? document.body : null
                    }
                  />

                  <PreferredCitySelect
                    cityOptions={cityOptions}
                    validation={{
                      values,
                      errors,
                      touched,
                      setFieldValue,
                      setFieldTouched,
                    }}
                    error={
                      touched.preferredCity && errors.preferredCity
                        ? errors.preferredCity
                        : null
                    }
                    onBlur={() => setFieldTouched("preferredCity", true)}
                  />

                  <CustomInput
                    label="Project Name"
                    name="projectName"
                    placeholder="e.g. XYZ Record Label"
                    value={values.projectName}
                    onChange={handleChange}
                    onBlur={() => setFieldTouched("projectName", true)}
                    error={touched.projectName && errors.projectName}
                  />

                  <CategoriesMultiselect
                    label="Categories"
                    helperText="Select all that apply"
                    options={categories}
                    selected={values.categories}
                    onChange={next => {
                      setFieldValue("categories", next)
                      setFieldTouched("categories", true, false)
                    }}
                    checkIconSrc={ICONS.check}
                    error={
                      touched.categories && typeof errors.categories === "string"
                        ? errors.categories
                        : null
                    }
                  />

                  <CustomDatePicker
                    label="Date"
                    startDate={values.startDate}
                    endDate={values.endDate}
                    onStartDateChange={value => {
                      setFieldValue("startDate", value)
                      setFieldTouched("startDate", true, false)
                    }}
                    onEndDateChange={value => {
                      setFieldValue("endDate", value)
                      setFieldTouched("endDate", true, false)
                    }}
                    isOngoing={values.isOngoing}
                    onOngoingChange={checked => {
                      setFieldValue("isOngoing", checked)
                      if (checked) setFieldValue("endDate", "")
                    }}
                    ongoingLabel="Still in this role"
                    calendarIconSrc={ICONS.calendar}
                    checkIconSrc={ICONS.check}
                    startError={Boolean(touched.startDate && errors.startDate)}
                    endError={Boolean(touched.endDate && errors.endDate)}
                    error={[
                      touched.startDate && errors.startDate,
                      !values.isOngoing && touched.endDate && errors.endDate,
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  />
                </div>
              </div>

              <div className="ongoing-role-modal__actions">
                <CustomButton
                  variant="danger"
                  size="md"
                  type="button"
                  className="ongoing-role-modal__cancel"
                  onClick={onClose}
                  disabled={isSubmitting || isLoadingDetail}
                >
                  Cancel
                </CustomButton>
                <CustomButton
                  variant="primary"
                  size="md"
                  type="submit"
                  className="ongoing-role-modal__submit"
                  loading={isSubmitting}
                  disabled={isSubmitting || isLoadingDetail}
                >
                  {isSubmitting
                    ? "Saving..."
                    : isEdit
                      ? "Save Changes"
                      : "Save Role"}
                </CustomButton>
              </div>
            </Form>
          )}
        </Formik>
      </ModalBody>
    </Modal>
  )
}

AddOngoingRoleModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  role: PropTypes.object,
  onSuccess: PropTypes.func,
}

export const AddOngoingRoleModalProvider = ({ children }) => {
  const [state, setState] = useState({
    isOpen: false,
    role: null,
    onSuccess: null,
  })

  const openAddOngoingRole = useCallback((options = {}) => {
    setState({
      isOpen: true,
      role: options.role || null,
      onSuccess: options.onSuccess || null,
    })
  }, [])

  const closeAddOngoingRole = useCallback(() => {
    setState({ isOpen: false, role: null, onSuccess: null })
  }, [])

  const value = useMemo(
    () => ({ openAddOngoingRole, closeAddOngoingRole }),
    [openAddOngoingRole, closeAddOngoingRole],
  )

  return (
    <AddOngoingRoleModalContext.Provider value={value}>
      {children}
      <AddOngoingRoleModal
        isOpen={state.isOpen}
        onClose={closeAddOngoingRole}
        role={state.role}
        onSuccess={state.onSuccess}
      />
    </AddOngoingRoleModalContext.Provider>
  )
}

AddOngoingRoleModalProvider.propTypes = {
  children: PropTypes.node,
}

export const useAddOngoingRoleModal = () => {
  const context = useContext(AddOngoingRoleModalContext)
  if (!context) {
    throw new Error(
      "useAddOngoingRoleModal must be used within AddOngoingRoleModalProvider",
    )
  }
  return context
}

export default AddOngoingRoleModal
